/**
 * PinPropertiesPanel Component
 *
 * Floating panel for editing the properties of a selected map pin.
 * Allows editing name, description, coordinates (read-only), scale, color,
 * icon, label, layer assignment, visibility, navigation target, and deletion.
 */

import React, { useState, useCallback } from 'react';
import { X, Trash2, Droplet, Camera, Activity, Zap, Home, Building, Warehouse, Droplets, Waves, CloudRain, FlaskConical, Thermometer, Wind, Sun, Wifi, RadioTower, Flame, AlertTriangle, Wrench, Gauge, Info, Eye, EyeOff, Database, Settings, Move } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MapPin as MapPinType, MapGroup, PinDataBinding } from '../MapComponent.types';
import {
  mapControlButtonClass,
  mapControlHeaderClass,
  mapControlInputClass,
  mapControlSurfaceClass,
  mapControlSubtleButtonClass,
} from './mapControlStyles';

// ============================================================================
// Constants
// ============================================================================

const COLOR_PRESETS = [
  '#EF4444', '#3B82F6', '#22C55E', '#EAB308', '#F97316',
  '#A855F7', '#EC4899', '#000000', '#6B7280',
] as const;

const ICON_OPTIONS: { type: NonNullable<MapPinType['style']['icon']>; label: string; Icon: React.FC<{ className?: string }> }[] = [
  { type: 'location', label: 'Drop', Icon: Droplet },
  { type: 'camera', label: 'Camera', Icon: Camera },
  { type: 'sensor', label: 'Sensor', Icon: Activity },
  { type: 'power', label: 'Power', Icon: Zap },
  { type: 'home', label: 'Home', Icon: Home },
  { type: 'building', label: 'Building', Icon: Building },
  { type: 'warehouse', label: 'Warehouse', Icon: Warehouse },
  { type: 'water', label: 'Water', Icon: Droplets },
  { type: 'waves', label: 'Waves', Icon: Waves },
  { type: 'rain', label: 'Rain', Icon: CloudRain },
  { type: 'flask', label: 'Flask', Icon: FlaskConical },
  { type: 'temperature', label: 'Temp', Icon: Thermometer },
  { type: 'wind', label: 'Wind', Icon: Wind },
  { type: 'sun', label: 'Sun', Icon: Sun },
  { type: 'wifi', label: 'Wifi', Icon: Wifi },
  { type: 'radio', label: 'Radio', Icon: RadioTower },
  { type: 'flame', label: 'Flame', Icon: Flame },
  { type: 'warning', label: 'Alert', Icon: AlertTriangle },
  { type: 'maintenance', label: 'Maint.', Icon: Wrench },
  { type: 'gauge', label: 'Gauge', Icon: Gauge },
  { type: 'info', label: 'Info', Icon: Info },
];

// ============================================================================
// Types
// ============================================================================

export interface PinPropertiesPanelProps {
  pin: MapPinType;
  groups: MapGroup[];
  pages: Array<{ id: string; name: string }>;
  onUpdatePin: (pinId: string, updates: Partial<MapPinType>) => void;
  onDeletePin: (pinId: string) => void;
  onRequestDataBinding?: (pinId: string) => void;
  onClose: () => void;
  isRepositionMode?: boolean;
  onToggleRepositionMode?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export const PinPropertiesPanel: React.FC<PinPropertiesPanelProps> = ({
  pin,
  groups,
  pages,
  onUpdatePin,
  onDeletePin,
  onRequestDataBinding,
  onClose,
  isRepositionMode,
  onToggleRepositionMode,
}) => {
  const [nameValue, setNameValue] = useState(pin.name);
  const [descriptionValue, setDescriptionValue] = useState(pin.description ?? '');
  const [labelValue, setLabelValue] = useState(pin.style.label ?? '');
  const [customColor, setCustomColor] = useState('');
  const [editingBindingId, setEditingBindingId] = useState<string | null>(null);

  // Sync inputs when selected pin changes
  React.useEffect(() => {
    setNameValue(pin.name);
    setDescriptionValue(pin.description ?? '');
    setLabelValue(pin.style.label ?? '');
  }, [pin.id, pin.name, pin.description, pin.style.label]);

  const handleNameBlur = useCallback(() => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== pin.name) {
      onUpdatePin(pin.id, { name: trimmed });
    }
  }, [nameValue, pin.id, pin.name, onUpdatePin]);

  const handleDescriptionBlur = useCallback(() => {
    const trimmed = descriptionValue.trim();
    if (trimmed !== (pin.description ?? '')) {
      onUpdatePin(pin.id, { description: trimmed || undefined });
    }
  }, [descriptionValue, pin.id, pin.description, onUpdatePin]);

  const handleLabelBlur = useCallback(() => {
    const trimmed = labelValue.trim();
    if (trimmed !== (pin.style.label ?? '')) {
      onUpdatePin(pin.id, { style: { ...pin.style, label: trimmed || undefined } });
    }
  }, [labelValue, pin.id, pin.style, onUpdatePin]);

  const handleNameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  }, []);

  const handleColorSelect = useCallback((color: string) => {
    onUpdatePin(pin.id, { style: { ...pin.style, color } });
  }, [pin.id, pin.style, onUpdatePin]);

  const handleCustomColorApply = useCallback(() => {
    const hex = customColor.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      handleColorSelect(hex);
      setCustomColor('');
    }
  }, [customColor, handleColorSelect]);

  const handleIconSelect = useCallback((icon: MapPinType['style']['icon']) => {
    onUpdatePin(pin.id, { style: { ...pin.style, icon } });
  }, [pin.id, pin.style, onUpdatePin]);

  const handleScaleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const scale = parseFloat(e.target.value);
    onUpdatePin(pin.id, { style: { ...pin.style, scale } });
  }, [pin.id, pin.style, onUpdatePin]);

  const handleVisibilityToggle = useCallback(() => {
    onUpdatePin(pin.id, { visible: !pin.visible });
  }, [pin.id, pin.visible, onUpdatePin]);

  const handleGroupChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const groupId = e.target.value || undefined;
    onUpdatePin(pin.id, { groupId });
  }, [pin.id, onUpdatePin]);

  const handleNavigationChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const targetPageId = e.target.value;
    if (!targetPageId) {
      onUpdatePin(pin.id, { navigation: undefined });
    } else {
      const page = pages.find(p => p.id === targetPageId);
      onUpdatePin(pin.id, {
        navigation: {
          targetPageId,
          targetPageName: page?.name,
        },
      });
    }
  }, [pin.id, pages, onUpdatePin]);

  const handleDelete = useCallback(() => {
    onDeletePin(pin.id);
  }, [pin.id, onDeletePin]);

  const handleRemoveBinding = useCallback((bindingId: string) => {
    const updated = (pin.dataBindings ?? []).filter(b => b.id !== bindingId);
    onUpdatePin(pin.id, { dataBindings: updated });
    if (editingBindingId === bindingId) setEditingBindingId(null);
  }, [pin.id, pin.dataBindings, onUpdatePin, editingBindingId]);

  const handleUpdateBinding = useCallback((bindingId: string, updates: Partial<PinDataBinding>) => {
    const updated = (pin.dataBindings ?? []).map(b =>
      b.id === bindingId ? { ...b, ...updates } : b
    );
    onUpdatePin(pin.id, { dataBindings: updated });
  }, [pin.id, pin.dataBindings, onUpdatePin]);

  return (
    <div
      className={cn(
        "absolute top-2.5 right-[60px] z-[15] w-[260px] text-sm max-h-[calc(100%-20px)] flex flex-col",
        mapControlSurfaceClass,
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className={cn("flex items-center gap-2 px-3 py-2 flex-shrink-0", mapControlHeaderClass)}>
        <span
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: pin.style.color }}
        />
        <span className="font-medium text-foreground truncate flex-1">{pin.name}</span>
        <button
          onClick={onClose}
          className={cn("p-0.5 rounded", mapControlButtonClass)}
          aria-label="Close pin properties"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="px-3 py-2 space-y-3 overflow-y-auto">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Name</label>
          <input
            type="text"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            className={cn("w-full px-2 py-1 text-sm rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400", mapControlInputClass)}
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Description</label>
          <textarea
            rows={2}
            value={descriptionValue}
            onChange={(e) => setDescriptionValue(e.target.value)}
            onBlur={handleDescriptionBlur}
            placeholder="Optional description..."
            className={cn("w-full px-2 py-1 text-sm rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 resize-none", mapControlInputClass)}
          />
        </div>

        {/* Coordinates (read-only) */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Coordinates</label>
          <div className={cn("flex gap-2 text-xs px-2 py-1.5 rounded", mapControlInputClass)}>
            <span>Lat: {pin.latitude?.toFixed(5) ?? '—'}</span>
            <span className="text-muted-foreground">|</span>
            <span>Lng: {pin.longitude?.toFixed(5) ?? '—'}</span>
          </div>
          {onToggleRepositionMode && (
            <button
              onClick={onToggleRepositionMode}
              className={cn(
                "w-full mt-1.5 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md transition-colors",
                isRepositionMode
                  ? 'border border-primary/40 bg-primary/15 text-primary'
                  : mapControlSubtleButtonClass,
              )}
            >
              <Move className="w-3 h-3" />
              {isRepositionMode ? 'Exit Reposition' : 'Reposition Pin'}
            </button>
          )}
        </div>

        {/* Scale / Size */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">
            Size <span className="text-muted-foreground font-normal">({(pin.style.scale ?? 1).toFixed(1)}x)</span>
          </label>
          <input
            type="range"
            min="0.5"
            max="2.0"
            step="0.1"
            value={pin.style.scale ?? 1}
            onChange={handleScaleChange}
            className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
            <span>0.5x</span>
            <span>2.0x</span>
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Color</label>
          <div className="flex flex-wrap gap-1.5">
            {COLOR_PRESETS.map((color) => (
              <button
                key={color}
                onClick={() => handleColorSelect(color)}
                className={cn(
                  "w-6 h-6 rounded-full border-2 transition-transform hover:scale-110",
                  pin.style.color === color ? 'border-primary scale-110' : 'border-border',
                )}
                style={{ backgroundColor: color }}
                aria-label={`Select color ${color}`}
              />
            ))}
            {/* Custom color input */}
            <div className="flex items-center gap-1 ml-1">
              <input
                type="text"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomColorApply()}
                placeholder="#hex"
                className={cn("w-14 px-1 py-0.5 text-xs rounded focus:outline-none focus:ring-1 focus:ring-blue-400", mapControlInputClass)}
              />
            </div>
          </div>
        </div>

        {/* Icon */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Icon</label>
          <div className="grid grid-cols-7 gap-1">
            {ICON_OPTIONS.map(({ type, label, Icon }) => (
              <button
                key={type}
                onClick={() => handleIconSelect(type)}
                className={cn(
                  "w-7 h-7 flex items-center justify-center rounded border transition-colors",
                  pin.style.icon === type
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground',
                )}
                aria-label={`Select ${label} icon`}
                title={label}
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
        </div>

        {/* Label */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Label</label>
          <input
            type="text"
            value={labelValue}
            onChange={(e) => setLabelValue(e.target.value)}
            onBlur={handleLabelBlur}
            onKeyDown={handleNameKeyDown}
            placeholder="Optional map label..."
            className={cn("w-full px-2 py-1 text-sm rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400", mapControlInputClass)}
          />
        </div>

        {/* Layer */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Layer</label>
          <select
            value={pin.groupId || ''}
            onChange={handleGroupChange}
            className={cn("w-full px-2 py-1 text-sm rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400", mapControlInputClass)}
          >
            <option value="">None</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </div>

        {/* Visibility */}
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-muted-foreground">Visibility</label>
          <button
            onClick={handleVisibilityToggle}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded border text-xs transition-colors",
              pin.visible !== false
                ? 'border-green-500/35 bg-green-500/12 text-green-300 hover:bg-green-500/18'
                : mapControlSubtleButtonClass,
            )}
            aria-label={pin.visible !== false ? 'Hide pin' : 'Show pin'}
          >
            {pin.visible !== false ? (
              <><Eye className="w-3.5 h-3.5" /> Visible</>
            ) : (
              <><EyeOff className="w-3.5 h-3.5" /> Hidden</>
            )}
          </button>
        </div>

        {/* Navigation */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Navigation</label>
          <select
            value={pin.navigation?.targetPageId || ''}
            onChange={handleNavigationChange}
            className={cn("w-full px-2 py-1 text-sm rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400", mapControlInputClass)}
          >
            <option value="">None</option>
            {pages.map((page) => (
              <option key={page.id} value={page.id}>{page.name}</option>
            ))}
          </select>
        </div>

        {/* Data Values */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Data Values</label>
          {(pin.dataBindings ?? []).length > 0 && (
            <div className="space-y-1.5 mb-2">
              {(pin.dataBindings ?? []).map((binding) => (
                <div key={binding.id} className="bg-muted/50 rounded border border-border px-2 py-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 min-w-0">
                      <Database className="w-3 h-3 text-blue-500 flex-shrink-0" />
                      <span className="text-xs font-medium text-foreground truncate">{binding.displayLabel}</span>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => setEditingBindingId(editingBindingId === binding.id ? null : binding.id)}
                        className={cn("p-0.5 rounded", mapControlButtonClass)}
                        aria-label="Edit binding format"
                        title="Format settings"
                      >
                        <Settings className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleRemoveBinding(binding.id)}
                        className="p-0.5 rounded text-muted-foreground hover:bg-red-500/10 hover:text-red-400"
                        aria-label="Remove data binding"
                        title="Remove"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{binding.valueField}</div>

                  {/* Inline formatting editor */}
                  {editingBindingId === binding.id && (
                    <div className="mt-1.5 pt-1.5 border-t border-border space-y-1.5">
                      <div className="flex gap-1.5">
                        <div className="flex-1">
                          <label className="text-[10px] text-muted-foreground block">Prefix</label>
                          <input
                            type="text"
                            value={binding.prefix ?? ''}
                            onChange={(e) => handleUpdateBinding(binding.id, { prefix: e.target.value || undefined })}
                            placeholder="e.g. $"
                            className={cn("w-full px-1 py-0.5 text-xs rounded focus:outline-none focus:ring-1 focus:ring-blue-400", mapControlInputClass)}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] text-muted-foreground block">Suffix</label>
                          <input
                            type="text"
                            value={binding.suffix ?? binding.unit ?? ''}
                            onChange={(e) => handleUpdateBinding(binding.id, { suffix: e.target.value || undefined, unit: undefined })}
                            placeholder="e.g. %"
                            className={cn("w-full px-1 py-0.5 text-xs rounded focus:outline-none focus:ring-1 focus:ring-blue-400", mapControlInputClass)}
                          />
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        <div className="flex-1">
                          <label className="text-[10px] text-muted-foreground block">Decimals</label>
                          <input
                            type="number"
                            min={0}
                            max={6}
                            value={binding.decimalPlaces ?? ''}
                            onChange={(e) => handleUpdateBinding(binding.id, { decimalPlaces: e.target.value ? Math.trunc(Number(e.target.value)) : undefined })}
                            placeholder="auto"
                            className={cn("w-full px-1 py-0.5 text-xs rounded focus:outline-none focus:ring-1 focus:ring-blue-400", mapControlInputClass)}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] text-muted-foreground block">Color</label>
                          <input
                            type="text"
                            value={binding.color ?? ''}
                            onChange={(e) => handleUpdateBinding(binding.id, { color: e.target.value || undefined })}
                            placeholder="#hex"
                            className={cn("w-full px-1 py-0.5 text-xs rounded focus:outline-none focus:ring-1 focus:ring-blue-400", mapControlInputClass)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {onRequestDataBinding && (
            <button
              onClick={() => onRequestDataBinding(pin.id)}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-primary bg-primary/10 hover:bg-primary/15 border border-primary/30 rounded transition-colors"
            >
              <Database className="w-3 h-3" />
              Add Data
            </button>
          )}
        </div>

        {/* Delete */}
        <button
          onClick={handleDelete}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm text-red-300 bg-red-500/10 hover:bg-red-500/15 border border-red-500/30 rounded transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete Pin
        </button>
      </div>
    </div>
  );
};

export default PinPropertiesPanel;
